// ABOUT: Invoice API service — wrapper for /api/invoicing/ endpoints

import api from './api';

const API_BASE = '/api/invoicing';

const invoicingService = {
  // Invoice CRUD and creation from service order
  getInvoice: (id) => api.get(`${API_BASE}/invoices/${id}/`),
  updateInvoice: (id, data) => api.patch(`${API_BASE}/invoices/${id}/`, data),
  createFromServiceOrder: (soId, viewType = 'service_type', tierId = null, componentIds = []) =>
    api.post(`${API_BASE}/invoices/from-service-order/${soId}/`, {
      view_type: viewType,
      ...(tierId ? { tier_id: tierId } : {}),
      ...(componentIds.length ? { component_ids: componentIds } : {}),
    }),

  // Invoice actions
  sendInvoice: (id, data = {}) => api.post(`${API_BASE}/invoices/${id}/send/`, data),
  resendInvoice: (id) => api.post(`${API_BASE}/invoices/${id}/resend/`),
  recalculateRates: (id) => api.post(`${API_BASE}/invoices/${id}/recalculate_rates/`),
  previewEmail: (id) => api.get(`${API_BASE}/invoices/${id}/preview_email/`),
  voidInvoice: (id, reason) => api.post(`${API_BASE}/invoices/${id}/void/`, { reason }),
  toggleSuppressReminders: (id) => api.post(`${API_BASE}/invoices/${id}/suppress-reminders/`),
  downloadCreditNotePDF: (id) => api.get(`${API_BASE}/invoices/${id}/credit-note/download-pdf/`),
  previewPDF: (id, includeHeader = true) => api.get(
    `${API_BASE}/invoices/${id}/preview_pdf/`,
    { params: { include_header: includeHeader }, responseType: 'blob' }
  ),
  downloadPDF: (id) => api.get(`${API_BASE}/invoices/${id}/download_pdf/`),

  // Payments
  recordPayment: (invoiceId, data) => api.post(`${API_BASE}/invoices/${invoiceId}/payments/`, data),
  updatePayment: (invoiceId, paymentId, data) => api.patch(`${API_BASE}/invoices/${invoiceId}/payments/${paymentId}/`, data),
  deletePayment: (invoiceId, paymentId) => api.delete(`${API_BASE}/invoices/${invoiceId}/payments/${paymentId}/`),

  // Line items
  createLineItem: (invoiceId, data) => api.post(`${API_BASE}/invoices/${invoiceId}/line-items/`, data),
  updateLineItem: (invoiceId, lineItemId, data) => api.patch(`${API_BASE}/invoices/${invoiceId}/line-items/${lineItemId}/`, data),
  deleteLineItem: (invoiceId, lineItemId) => api.delete(`${API_BASE}/invoices/${invoiceId}/line-items/${lineItemId}/`),

  // Job-level invoicing
  getInvoiceableJobs: (soId, showAll = false) =>
    api.get(`${API_BASE}/invoices/invoiceable-jobs/${soId}/`, { params: { show_all: showAll } }),
  createFromJobs: (soId, jobIds, viewType = 'service_type', tierId = null, componentIds = []) =>
    api.post(`${API_BASE}/invoices/from-jobs/${soId}/`, {
      job_ids: jobIds,
      view_type: viewType,
      ...(tierId ? { tier_id: tierId } : {}),
      ...(componentIds.length ? { component_ids: componentIds } : {}),
    }),
  createFromMultipleJobs: (jobIds, viewType = 'service_type', tierId = null, componentIds = []) =>
    api.post(`${API_BASE}/invoices/from-multiple-jobs/`, {
      job_ids: jobIds,
      view_type: viewType,
      ...(tierId ? { tier_id: tierId } : {}),
      ...(componentIds.length ? { component_ids: componentIds } : {}),
    }),

  // View preferences master data
  getPricingTiers: () => api.get(`${API_BASE}/pricing-tiers/`),
  updatePricingTier: (id, data) => api.patch(`${API_BASE}/pricing-tiers/${id}/`, data),
  getComponents: (params) => api.get(`${API_BASE}/components/`, { params }),
  createComponent: (data) => api.post(`${API_BASE}/components/`, data),
  updateComponent: (id, data) => api.patch(`${API_BASE}/components/${id}/`, data),
  deleteComponent: (id) => api.delete(`${API_BASE}/components/${id}/`),

  // Invoice approval
  approveInvoice: (id) => api.post(`${API_BASE}/invoices/${id}/approve/`),

  // AR Dashboard
  getARDashboard: () => api.get(`${API_BASE}/ar-dashboard/`),
  getARInvoices: (params) => api.get(`${API_BASE}/ar-dashboard/invoices/`, { params }),
  getARCreditNotes: (params) => api.get(`${API_BASE}/ar-dashboard/credit-notes/`, { params }),
  getUninvoicedSOs: (params) => api.get(`${API_BASE}/ar-dashboard/uninvoiced-sos/`, { params }),

  // Monthly consolidated invoicing
  getMonthlyBillingCompanies: (params) => api.get(`${API_BASE}/monthly-invoice/companies/`, { params }),
  getMonthlyInvoicePreview: (companyId, year, month) =>
    api.get(`${API_BASE}/monthly-invoice/preview/`, { params: { company_id: companyId, year, month } }),
  createMonthlyInvoice: (companyId, year, month) =>
    api.post(`${API_BASE}/monthly-invoice/`, { company_id: companyId, year, month }),

  // Settings
  getSettings: () => api.get(`${API_BASE}/settings/`),
  updateSettings: (data) => api.patch(`${API_BASE}/settings/`, data),
};

export default invoicingService;
